"use client";

import { motion } from "framer-motion";
import {
	SiAmazon,
	SiCloudflare,
	SiDocker,
	SiFirebase,
	SiGithub,
	SiGooglecloud,
	SiKubernetes,
	SiNetlify,
	SiSupabase,
	SiVercel,
} from "react-icons/si";

type IconNode = {
	icon:
		| "docker"
		| "kubernetes"
		| "googlecloud"
		| "aws"
		| "firebase"
		| "supabase"
		| "vercel"
		| "cloudflare"
		| "netlify"
		| "github";
	className: string;
	duration: number;
	delay: number;
	yDrift: number;
	xDrift: number;
	rotate: number;
};

const RIGHT_FOCUS: IconNode[] = [
	{
		icon: "docker",
		className: "absolute right-[20%] top-[16%] h-16 w-16 text-[rgba(36,150,237,0.52)]",
		duration: 7.8,
		delay: 0.2,
		yDrift: 10,
		xDrift: 4,
		rotate: 4,
	},
	{
		icon: "kubernetes",
		className: "absolute right-[10%] top-[29%] h-20 w-20 text-[rgba(50,108,229,0.5)]",
		duration: 9.2,
		delay: 0.7,
		yDrift: 12,
		xDrift: -3,
		rotate: -5,
	},
	{
		icon: "docker",
		className: "absolute right-[26%] top-[43%] h-12 w-12 text-[rgba(36,150,237,0.45)]",
		duration: 8.4,
		delay: 0.35,
		yDrift: 8,
		xDrift: 3,
		rotate: 3,
	},
	{
		icon: "kubernetes",
		className: "absolute right-[16%] top-[56%] h-14 w-14 text-[rgba(50,108,229,0.42)]",
		duration: 8.9,
		delay: 0.15,
		yDrift: 10,
		xDrift: -4,
		rotate: -4,
	},
	{
		icon: "docker",
		className: "absolute right-[8%] top-[70%] h-10 w-10 text-[rgba(36,150,237,0.38)]",
		duration: 7.2,
		delay: 0.5,
		yDrift: 7,
		xDrift: 2,
		rotate: 3,
	},
	{
		icon: "googlecloud",
		className: "absolute right-[31%] top-[22%] h-12 w-12 text-[rgba(66,133,244,0.44)]",
		duration: 8.1,
		delay: 0.4,
		yDrift: 9,
		xDrift: -3,
		rotate: 4,
	},
	{
		icon: "aws",
		className: "absolute right-[6%] top-[46%] h-11 w-11 text-[rgba(255,153,0,0.45)]",
		duration: 9.1,
		delay: 0.6,
		yDrift: 8,
		xDrift: 2,
		rotate: -4,
	},
	{
		icon: "firebase",
		className: "absolute right-[22%] top-[68%] h-11 w-11 text-[rgba(255,202,40,0.48)]",
		duration: 8.6,
		delay: 0.22,
		yDrift: 7,
		xDrift: -2,
		rotate: 3,
	},
	{
		icon: "supabase",
		className: "absolute right-[14%] top-[80%] h-9 w-9 text-[rgba(62,207,142,0.44)]",
		duration: 7.9,
		delay: 0.32,
		yDrift: 6,
		xDrift: 2,
		rotate: -3,
	},
	{
		icon: "vercel",
		className: "absolute right-[30%] top-[84%] h-9 w-9 text-[rgba(245,245,245,0.4)]",
		duration: 7.5,
		delay: 0.18,
		yDrift: 5,
		xDrift: -2,
		rotate: 2,
	},
	{
		icon: "cloudflare",
		className: "absolute right-[3%] top-[20%] h-10 w-10 text-[rgba(246,130,31,0.44)]",
		duration: 8.7,
		delay: 0.16,
		yDrift: 8,
		xDrift: 2,
		rotate: -3,
	},
	{
		icon: "netlify",
		className: "absolute right-[28%] top-[54%] h-10 w-10 text-[rgba(0,173,181,0.42)]",
		duration: 8.3,
		delay: 0.27,
		yDrift: 7,
		xDrift: -2,
		rotate: 3,
	},
	{
		icon: "github",
		className: "absolute right-[17%] top-[6%] h-10 w-10 text-[rgba(230,237,243,0.44)]",
		duration: 7.7,
		delay: 0.46,
		yDrift: 6,
		xDrift: 2,
		rotate: -2,
	},
];

const LEFT_DUST: IconNode[] = [
	{
		icon: "docker",
		className: "absolute left-[6%] top-[14%] h-6 w-6 text-[rgba(36,150,237,0.32)]",
		duration: 6.6,
		delay: 0.1,
		yDrift: 6,
		xDrift: 2,
		rotate: 4,
	},
	{
		icon: "kubernetes",
		className: "absolute left-[12%] top-[28%] h-5 w-5 text-[rgba(50,108,229,0.3)]",
		duration: 7.4,
		delay: 0.45,
		yDrift: 5,
		xDrift: -2,
		rotate: -5,
	},
	{
		icon: "docker",
		className: "absolute left-[18%] top-[41%] h-4 w-4 text-[rgba(36,150,237,0.28)]",
		duration: 6.9,
		delay: 0.25,
		yDrift: 5,
		xDrift: 2,
		rotate: 4,
	},
	{
		icon: "kubernetes",
		className: "absolute left-[9%] top-[57%] h-5 w-5 text-[rgba(50,108,229,0.3)]",
		duration: 7.8,
		delay: 0.5,
		yDrift: 6,
		xDrift: -2,
		rotate: -4,
	},
	{
		icon: "docker",
		className: "absolute left-[15%] top-[73%] h-4 w-4 text-[rgba(36,150,237,0.24)]",
		duration: 6.8,
		delay: 0.3,
		yDrift: 4,
		xDrift: 1,
		rotate: 3,
	},
	{
		icon: "googlecloud",
		className: "absolute left-[20%] top-[18%] h-5 w-5 text-[rgba(66,133,244,0.3)]",
		duration: 7.2,
		delay: 0.35,
		yDrift: 5,
		xDrift: -2,
		rotate: 4,
	},
	{
		icon: "aws",
		className: "absolute left-[4%] top-[36%] h-4 w-4 text-[rgba(255,153,0,0.27)]",
		duration: 6.7,
		delay: 0.2,
		yDrift: 4,
		xDrift: 1,
		rotate: -3,
	},
	{
		icon: "firebase",
		className: "absolute left-[22%] top-[54%] h-4 w-4 text-[rgba(255,202,40,0.28)]",
		duration: 7.1,
		delay: 0.42,
		yDrift: 4,
		xDrift: -1,
		rotate: 3,
	},
	{
		icon: "supabase",
		className: "absolute left-[7%] top-[68%] h-4 w-4 text-[rgba(62,207,142,0.28)]",
		duration: 6.9,
		delay: 0.28,
		yDrift: 4,
		xDrift: 1,
		rotate: -2,
	},
	{
		icon: "vercel",
		className: "absolute left-[17%] top-[83%] h-4 w-4 text-[rgba(245,245,245,0.24)]",
		duration: 7.4,
		delay: 0.5,
		yDrift: 3,
		xDrift: -1,
		rotate: 2,
	},
	{
		icon: "cloudflare",
		className: "absolute left-[3%] top-[22%] h-5 w-5 text-[rgba(246,130,31,0.3)]",
		duration: 6.8,
		delay: 0.24,
		yDrift: 4,
		xDrift: 1,
		rotate: -3,
	},
	{
		icon: "netlify",
		className: "absolute left-[24%] top-[66%] h-4 w-4 text-[rgba(0,173,181,0.27)]",
		duration: 7.3,
		delay: 0.38,
		yDrift: 3,
		xDrift: -1,
		rotate: 2,
	},
	{
		icon: "github",
		className: "absolute left-[10%] top-[9%] h-5 w-5 text-[rgba(230,237,243,0.3)]",
		duration: 7,
		delay: 0.31,
		yDrift: 4,
		xDrift: 1,
		rotate: -2,
	},
];

function TechIcon({ node }: { node: IconNode }) {
	const ICON_MAP = {
		docker: SiDocker,
		kubernetes: SiKubernetes,
		googlecloud: SiGooglecloud,
		aws: SiAmazon,
		firebase: SiFirebase,
		supabase: SiSupabase,
		vercel: SiVercel,
		cloudflare: SiCloudflare,
		netlify: SiNetlify,
		github: SiGithub,
	} as const;
	const Icon = ICON_MAP[node.icon];

	return (
		<motion.div
			className={node.className}
			animate={{
				y: [0, -node.yDrift, 0, node.yDrift * 0.45, 0],
				x: [0, node.xDrift, 0, -node.xDrift * 0.6, 0],
				rotate: [0, node.rotate, 0, -node.rotate * 0.5, 0],
				opacity: [1, 0.92, 1, 0.88, 1],
			}}
			transition={{
				duration: node.duration,
				delay: node.delay,
				repeat: Number.POSITIVE_INFINITY,
				ease: "easeInOut",
			}}
		>
			<Icon className="h-full w-full" />
		</motion.div>
	);
}

export default function ContactBgTechIcons() {
	return (
		<div className="absolute inset-0">
			<div className="absolute inset-0 bg-[radial-gradient(circle_at_82%_26%,rgba(197,211,230,0.11),transparent_38%),radial-gradient(circle_at_74%_78%,rgba(145,160,183,0.08),transparent_34%)]" />
			{RIGHT_FOCUS.map((node) => (
				<TechIcon key={`${node.icon}-${node.className}`} node={node} />
			))}
			{LEFT_DUST.map((node) => (
				<TechIcon key={`${node.icon}-${node.className}`} node={node} />
			))}
		</div>
	);
}
